import { useCallback, useRef, useState } from "react";
import { useCreateMarketDialogueMutation } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { departmentsForNewEvent } from "../departments/eventCreationDepartments";
import { useApiError } from "../errors/useApiError";

export const useCreateMarketDialogue = (onCreated: (marketDialogueId: string) => void) => {
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [create, { isLoading }] = useCreateMarketDialogueMutation();
  const { trigger } = useApiError();
  const [isChoosingDepartment, setIsChoosingDepartment] = useState(false);
  const requestInFlight = useRef(false);
  const departments = departmentsForNewEvent(selectedOrg);

  const createWith = useCallback(
    async (homeDepartmentId?: string) => {
      if (requestInFlight.current) return;
      requestInFlight.current = true;
      try {
        const dialogue = await create({ createMarketDialogueRequest: { title: "", homeDepartmentId } }).unwrap();
        setIsChoosingDepartment(false);
        onCreated(dialogue.id);
      } catch (e) {
        trigger(e);
      } finally {
        requestInFlight.current = false;
      }
    },
    [create, onCreated, trigger],
  );

  const start = useCallback(() => {
    if (requestInFlight.current) return;
    if (departments.length === 1) return void createWith(departments[0].id);
    setIsChoosingDepartment(true);
  }, [createWith, departments]);

  return {
    start,
    canCreate: departments.length > 0,
    isCreating: isLoading,
    isChoosingDepartment,
    departments,
    cancelChoosingDepartment: () => setIsChoosingDepartment(false),
    confirmDepartment: (departmentId: string) => void createWith(departmentId),
  };
};
